import { getBrowserLanguage } from '@nzyme/dom-utils/getBrowserLanguage.js';
import { getCookie } from '@nzyme/dom-utils/getCookie.js';
import { isBrowser } from '@nzyme/dom-utils/isBrowser.js';
import { setCookie } from '@nzyme/dom-utils/setCookie.js';
import { defineService } from '@nzyme/ioc/Service.js';
import { getSingleItem } from '@nzyme/utils/array/getSingleItem.js';
import { computed, ref } from 'vue';
import { useRouter } from 'vue-router';
import { Language } from '@sigma/common/i18n/Language.js';
import { formatLocale, parseLocale } from '@sigma/common/i18n/Locale.js';
import { DEFAULT_COUNTRY } from '@sigma/env/resources.js';
import { useLocalizedRoute } from '../routes/useLocalizedRoute.js';
const DEFAULT_LOCALE = formatLocale(DEFAULT_COUNTRY, 'en');
/**
 * Service that detects the current locale based on browser preferences, route, and cookies.
 */
export const BrowserLocaleContext = defineService({
    name: 'BrowserLocaleContext',
    setup() {
        const preferredLocale = ref(getPreferredLocale());
        const routeLocale = computed(getRouteLocale);
        const queryLocale = computed(getQueryLocale);
        const localizedRoute = useLocalizedRoute();
        const router = useRouter();
        const locale = computed(() => {
            return routeLocale.value || queryLocale.value || preferredLocale.value || DEFAULT_LOCALE;
        });
        if (isBrowser()) {
            // If we are in localized route, and locale is changed using query param,
            // we need to redirect to the new localized route
            if (routeLocale.value && queryLocale.value && routeLocale.value !== queryLocale.value) {
                const url = router.resolve(localizedRoute(queryLocale.value));
                window.location.href = url.href;
            }
            else if (locale.value !== preferredLocale.value) {
                setPreferredLocale(locale.value);
            }
        }
        return {
            locale,
            set: setPreferredLocale,
        };
        function getPreferredLocale() {
            const cookie = getCookie('locale');
            if (cookie && parseLocale(cookie)) {
                return cookie;
            }
            // Detect from browser language, use default country
            const browserLang = getBrowserLanguage(Language.options);
            if (browserLang) {
                return formatLocale(DEFAULT_COUNTRY, browserLang);
            }
            return undefined;
        }
        function getQueryLocale() {
            const queryLocaleValue = getSingleItem(router.currentRoute.value.query.locale);
            if (queryLocaleValue && parseLocale(queryLocaleValue)) {
                return queryLocaleValue;
            }
            return undefined;
        }
        function getRouteLocale() {
            return router.currentRoute.value.meta.locale;
        }
        function setPreferredLocale(newLocale) {
            preferredLocale.value = newLocale;
            setCookie('locale', newLocale, {
                expires: new Date(Date.now() + 1000 * 60 * 60 * 24 * 365),
                path: '/',
                sameSite: 'Lax',
            });
        }
    },
});
//# sourceMappingURL=BrowserLocaleContext.js.map